/**
 * SYNC CANÔNICO DE VIDEOWALL — onde um player DEVERIA estar no loop agora.
 *
 * <p>Vive na camada de serviço porque é regra de domínio, não de tela: a store de
 * playback precisa dela pra escolher o índice INICIAL (antes do primeiro render) e a
 * cena precisa dela pra corrigir durante a reprodução. Se ficasse em `scenes/`, a store
 * teria de importar de cima pra baixo.</p>
 */
export function resolveLoopTarget(p: {
  playbackStartedAt: string | undefined | null;
  durations: number[];
  nowMs: number;
}): {
  index: number;
  offsetSeconds: number;
  loopPosition: number;
  loopTotal: number;
} | null {
  if (!p.playbackStartedAt) return null;

  const epoch = new Date(p.playbackStartedAt).getTime();
  if (!Number.isFinite(epoch)) return null;

  const loopTotal = p.durations.reduce((sum, d) => sum + (d > 0 ? d : 0), 0);
  if (loopTotal <= 0) return null;

  const elapsed = (p.nowMs - epoch) / 1000;
  // Módulo protegido: época no futuro (relógio do device atrasado) daria negativo,
  // e um loopPosition negativo mandaria o player pro item errado.
  const loopPosition = ((elapsed % loopTotal) + loopTotal) % loopTotal;

  let acc = 0;
  for (let i = 0; i < p.durations.length; i++) {
    const dur = p.durations[i] > 0 ? p.durations[i] : 0;
    if (loopPosition < acc + dur) {
      return {
        index: i,
        offsetSeconds: loopPosition - acc,
        loopPosition,
        loopTotal,
      };
    }
    acc += dur;
  }

  // Só chega aqui por arredondamento no limite exato do loop.
  return {
    index: Math.max(0, p.durations.length - 1),
    offsetSeconds: 0,
    loopPosition,
    loopTotal,
  };
}

/**
 * AVANÇO DE COMPENSAÇÃO DE PINTURA, por plataforma (ms).
 *
 * <p>O player decide a troca no instante certo, mas o PIXEL aparece depois: decode,
 * composição e — na TV — o processamento do próprio painel. Medido em 2026-09-14 com os
 * três players na mesma campanha: pelo relógio interno a TV estava a 0,14s do navegador
 * (−0,59s vs −0,45s de média), mas na tela o operador via ~1s de atraso. Essa diferença
 * é invisível à telemetria, que mede antes do pixel.</p>
 *
 * <p>Compensar = mirar um pouco À FRENTE da época, pro quadro chegar no tempo certo. Mesmo
 * princípio do offset de sincronismo de áudio/vídeo.</p>
 *
 * <p>⚠️ Isto NÃO adianta a troca de um vídeo: o slot de vídeo acaba quando o vídeo acaba.
 * Age só nos caminhos CORRETIVOS — o pulso, o seek de entrada e o encurtamento da imagem.
 * Em plataforma que avança no fim natural (tablet/navegador), mexer aqui muda quando o
 * seek de entrada dispara, não o instante da troca.</p>
 *
 * <p>Ajustável em build por `VITE_SYNC_PAINT_LEAD_MS` — é um valor calibrado por observação
 * (o olho do operador é o único instrumento que enxerga a latência do painel), então
 * precisa ser fácil de mexer sem tocar no código.</p>
 */
export function paintLeadMs(platform: string): number {
  const override = Number(import.meta.env.VITE_SYNC_PAINT_LEAD_MS);
  if (Number.isFinite(override) && override >= 0) return override;
  switch (platform) {
    case 'tizen':
    case 'webos':
      return 1290; // painel de TV — calibrado no olho, na bancada (900/1000 atrasavam; 1400 adiantava)
    case 'android':
    case 'embedded':
      // WebView do tablet: pinta rápido, mas não instantâneo. O `embedded` é a MESMA
      // WebView, dentro do APK do parceiro — sem este caso caía no `default` e era
      // compensado como NAVEGADOR. Medido na bancada em 2026-09-30, decisão→1º quadro:
      // 216ms no embedded (n=39) contra 22ms no navegador (n=16). Compartilhar o caso com
      // `android` é também o que manda a regra de paridade com a frota.
      return 250;
    default:
      return 200; // navegador
  }
}

/**
 * Quanto o AVPlay gasta entre a DECISÃO de trocar e o 1º quadro decodificado, na TV.
 *
 * <p>Medido na bancada em 2026-09-30, n=15: mediana 1127ms (1074–1265), dos quais 506ms
 * (n=17, ±3ms) são o próprio contador `first frame` do AVPlay. É o custo de abrir e
 * preparar o plano de hardware — praticamente constante, por isso cabe como número fixo.</p>
 */
export const AVPLAY_OPEN_TO_FIRST_FRAME_MS = 1_130;

/**
 * Compensação de pintura **no 1º quadro do AVPlay**, onde o pipeline JÁ FOI PAGO.
 *
 * <p>{@link paintLeadMs} compensa o caminho inteiro decisão→pixel. No seek de entrada esse
 * caminho já correu: o quadro está decodificado. Usar o valor cheio ali soma de novo um
 * tempo que já passou e joga o seek à frente — foi o que a 1.0.451 fez, levando a TV de
 * 2,09s ATRASADA para 1,29s ADIANTADA contra o tablet (medido, n=11). Sobra só a latência
 * do PAINEL, que o pipeline não cobre.</p>
 *
 * <p>Deriva do mesmo valor calibrado no olho, então mexer no `VITE_SYNC_PAINT_LEAD_MS`
 * continua ajustando os dois caminhos de forma coerente.</p>
 */
export function avplayEntryLeadMs(platform: string): number {
  return Math.max(0, paintLeadMs(platform) - AVPLAY_OPEN_TO_FIRST_FRAME_MS);
}
